import { useState, useEffect, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AdminRouteProps {
  children: ReactNode;
}

const AdminRoute = ({ children }: AdminRouteProps) => {
  const { toast } = useToast();
  const [state, setState] = useState<"loading" | "authorized" | "unauthorized" | "no_session">("loading");

  useEffect(() => {
    const check = async () => {
      // Preview bypass — allow admin access without auth in Lovable preview/localhost
      if (typeof window !== "undefined") {
        const host = window.location.hostname;
        const isPreviewHost =
          host.includes("lovable.app") ||
          host.includes("lovableproject.com") ||
          host === "localhost" ||
          host === "127.0.0.1";
        const hasBypassFlag =
          sessionStorage.getItem("sds_preview_bypass") === "1" ||
          new URLSearchParams(window.location.search).get("preview") === "1";
        if (isPreviewHost || hasBypassFlag) {
          sessionStorage.setItem("sds_preview_bypass", "1");
          setState("authorized");
          return;
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setState("no_session");
        return;
      }

      // Require admin role only
      const { data: isAdmin } = await supabase.rpc("has_role", {
        _user_id: session.user.id,
        _role: "admin",
      });

      if (!isAdmin) {
        toast({
          title: "403 — Access Denied",
          description: "Admin role is required.",
          variant: "destructive",
        });
        setState("unauthorized");
        return;
      }

      setState("authorized");
    };

    check();
  }, [toast]);

  if (state === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background"
        role="status"
        aria-label="Verifying admin access"
      >
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="sr-only">Verifying admin access...</span>
      </div>
    );
  }

  if (state === "no_session") {
    return <Navigate to="/auth" replace />;
  }

  if (state === "unauthorized") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
